import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGame } from '../state/GameProvider';
import { DrawingCanvas } from '../components/DrawingCanvas';

const PERSIST_KEY = 'share-paint:identity';
const hasIdentity = () =>
  typeof localStorage !== 'undefined' && !!localStorage.getItem(PERSIST_KEY);

// Mismo sistema de coordenadas lógico que el Game (900×600). Mantenerlo estable
// garantiza que los trazos almacenados en el servidor se rendericen completos
// en cada tarjeta de votación sin recortes por mismatch de coord space.
const CANVAS_W = 900;
const CANVAS_H = 600;

export function Voting() {
  const { code } = useParams<{ code: string }>();
  const nav = useNavigate();
  const { state, castVote } = useGame();

  useEffect(() => {
    if (state.status === 'REVEAL' && code) nav(`/reveal/${code}`, { replace: true });
  }, [state.status, nav, code]);

  // Si tras 2.5s seguimos sin sesión NI identidad → volver al inicio.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!state.session && state.finalDrawings.length === 0 && !hasIdentity()) {
        nav('/', { replace: true });
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [state.session, state.finalDrawings.length, nav]);

  // Mostrar errores del servidor (p.ej. ALREADY_VOTED) si los hubiera
  const lastError = state.errors[state.errors.length - 1];

  if (!state.session || !state.me) {
    return <div className="min-h-screen flex items-center justify-center text-white/60">Cargando…</div>;
  }

  const me = state.me;
  const players = state.session.players;
  const drawings = state.finalDrawings;
  const myDrawingId = useMemo(
    () => drawings.find((d) => d.authorId === me.id)?.id,
    [drawings, me.id],
  );

  const submit = (drawingId: string) => {
    if (state.myVote) return;
    castVote(drawingId);
  };

  const allVoted = state.voters.voted >= state.voters.total;

  return (
    <main className="min-h-screen p-6 max-w-6xl mx-auto">
      <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase text-white/40">Fase de votación</p>
          <h2 className="text-3xl font-bold">Elige tu favorito</h2>
          <p className="text-sm text-white/60 mt-1">
            Cada jugador vota <strong>una sola vez</strong>. Los nombres están ocultos hasta la revelación.
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-white/40 uppercase">Votos</p>
          <p className="text-2xl font-mono">{state.voters.voted}/{state.voters.total}</p>
        </div>
      </header>

      {lastError && (
        <div className="card border-red-500/40 bg-red-500/10 text-red-200 text-sm mb-4">
          {lastError.message}
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {drawings.map((d, idx) => {
          const isOwn = d.id === myDrawingId;
          const isSelected = state.myVote === d.id;
          const canPick = !state.myVote;
          return (
            <div
              key={d.id}
              className={`card relative ${isSelected ? 'ring-2 ring-accent' : ''}`}
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-white/60">
                  Dibujo #{idx + 1}
                </p>
                {isOwn && (
                  <span className="text-xs px-2 py-1 rounded-full bg-white/5 text-white/60 border border-white/10">
                    🏷 Tu dibujo
                  </span>
                )}
                {isSelected && <span className="text-xs text-accent">✓ Tu voto</span>}
              </div>

              <div className="flex justify-center bg-white/5 rounded-lg p-2">
                <DrawingCanvas
                  width={CANVAS_W}
                  height={CANVAS_H}
                  strokes={d.strokes}
                  myUserId={me.id}
                  players={players}
                  disabled
                />
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-white/40">{d.strokes.length} trazos</span>
                <button
                  disabled={!canPick}
                  onClick={() => submit(d.id)}
                  className={isSelected ? 'btn-primary' : 'btn-ghost'}
                >
                  {isSelected ? 'Votado' : 'Elegir'}
                </button>
              </div>
            </div>
          );
        })}
      </section>

      <div className="mt-6 text-center text-sm text-white/60">
        {state.myVote
          ? '✓ Voto registrado — esperando al resto…'
          : allVoted
          ? 'Todos han votado — esperando revelación…'
          : 'Selecciona un dibujo (también puedes votar el tuyo).'}
      </div>
    </main>
  );
}
